import { create } from 'zustand';

/** Playback mode for the timeline. */
type TimelineMode = 'live' | 'historical';

/** Bottom-timeline scrubber state. */
interface TimelineState {
  mode: TimelineMode;
  /** Current playback time (epoch ms). */
  cursor: number;
  /** Earliest scrubbable time (epoch ms). */
  rangeStart: number;
  /** Latest time = now (epoch ms). */
  rangeEnd: number;
  /** Whether historical playback is advancing. */
  playing: boolean;
  /** Playback speed multiplier. */
  speed: number;

  /** Update the available range; keeps the cursor pinned to now while live. */
  setRange: (start: number, end: number) => void;
  /** Enter historical mode at a specific time. */
  scrubTo: (cursor: number) => void;
  /** Toggle play/pause (historical mode). */
  togglePlay: () => void;
  /** Set the playback speed. */
  setSpeed: (speed: number) => void;
  /** Return to live tailing. */
  goLive: () => void;
  /** Advance the cursor during playback; returns to live at the end. */
  advance: (deltaMs: number) => void;
}

/** Store driving the bottom timeline scrubber. */
export const useTimelineStore = create<TimelineState>((set) => ({
  mode: 'live',
  cursor: Date.now(),
  rangeStart: Date.now(),
  rangeEnd: Date.now(),
  playing: false,
  speed: 1,

  setRange: (start, end) =>
    set((state) => ({ rangeStart: start, rangeEnd: end, cursor: state.mode === 'live' ? end : state.cursor })),

  scrubTo: (cursor) => set({ mode: 'historical', cursor, playing: false }),

  togglePlay: () => set((state) => ({ playing: state.mode === 'historical' ? !state.playing : false })),

  setSpeed: (speed) => set({ speed }),

  goLive: () => set((state) => ({ mode: 'live', playing: false, cursor: state.rangeEnd })),

  advance: (deltaMs) =>
    set((state) => {
      const next = state.cursor + deltaMs * state.speed;
      if (next >= state.rangeEnd) return { mode: 'live', playing: false, cursor: state.rangeEnd };
      return { cursor: next };
    }),
}));
